import { useState } from 'react';
import { Description, ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  Box,
  MenuItem
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { BulkDownloadTable, SMBSelect } from '../../../components';
import {
  getSampleContractDocuments,
  getSiteReports,
  postSampleContractDocuments
} from '../../../fetch';
import { LoadingButton } from '@mui/lab';
import { IDropdownResponse, ISampleContractDocuments, ISiteReport } from '../../../models';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { SalesNeighborhoodDropdown } from '../../../components/dropdowns/sales-neighborhood-dropdown';
import { getSalesLookupElevations } from '../../../fetch/lookups/sales-portal';
import { SalesPlansDropdown } from '../../../components/dropdowns/sales-plans-dropdown';

export const SampleContracts = ({ shouldSeeAllNeighborhoods }: { shouldSeeAllNeighborhoods: boolean }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [siteReports, setSiteReports] = useState<ISiteReport[]>();
  const [elevationList, setElevationList] = useState<IDropdownResponse[]>();
  const [documentTypes, setDocumentTypes] = useState<ISampleContractDocuments[]>();
  const [selectedDocs, setSelectedDocs] = useState<ISampleContractDocuments[]>([]);
  const [isGeneratingDocs, setIsGeneratingDocs] = useState<boolean>(false);

  const fetchSiteReports = async val => {
    try {
      setIsLoading(true);
      const res = await getSiteReports(val, { isActive: true });
      setSiteReports?.(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading Site Reports, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchElevationsList = async (val, neighborhoodSeriesId) => {
    try {
      setIsLoading(true);
      const res = await getSalesLookupElevations(val, true, neighborhoodSeriesId);
      setElevationList(res);
    } catch (error) {
      enqueueSnackbar(`Error loading Elevations List, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6">Sample Contract Documents</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <Box mt={1}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  neighborhoods: '',
                  lot: '',
                  jobId: '',
                  plans: '',
                  elevations: ''
                }}
                onSubmit={async (values, actions) => {
                  try {
                    setIsGeneratingDocs(true);
                    const res = await getSampleContractDocuments({
                      jobId: Number(values.jobId),
                      planId: Number(values.plans),
                      planElevationId: Number(values.elevations)
                    });
                    setDocumentTypes(res);
                    setSelectedDocs([]);
                  } catch (error) {
                    console.log(error);
                    if (error?.response?.data?.Detail) {
                      enqueueSnackbar(error.response.data.Detail, {
                        variant: 'error'
                      });
                      // Fallback case if no error is provided.
                    } else {
                      enqueueSnackbar('Cannot generate contract documents.', {
                        variant: 'error'
                      });
                    }
                  } finally {
                    setIsGeneratingDocs(false);
                  }
                }}
              >
                {({
                  isSubmitting,
                  values,
                  initialValues,
                  setFieldValue,
                  handleSubmit,
                  dirty,
                  isValid,
                  handleBlur,
                  errors,
                  touched
                }) => {
                  return (
                    <>
                      <Form onSubmit={handleSubmit} autoComplete="none">
                        <Grid container spacing={1} flexWrap={{ xs: 'wrap', xl: 'nowrap' }}>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesNeighborhoodDropdown
                              name={'neighborhoods'}
                              isRequired={true}
                              isDisabled={isLoading}
                              value={values.neighborhoods}
                              handleChange={val => {
                                setFieldValue('neighborhoods', val);
                                setFieldValue('lot', '');
                                setFieldValue('jobId', '');
                                setFieldValue('plans', '');
                                setFieldValue('elevations', '');
                                setDocumentTypes([]);
                                val && fetchSiteReports(val);
                              }}
                              apiParams={{
                                isActive: true,
                                hasAvailableLots: true,
                                allNeighborhoods: shouldSeeAllNeighborhoods
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <FormControl fullWidth variant="outlined" size="medium" required>
                              <SMBSelect
                                disabled={!values.neighborhoods || isLoading}
                                name="lot"
                                label={'Lot'}
                                value={values.lot}
                                variant="outlined"
                                defaultValue={''}
                                required
                                onChange={e => {
                                  const jobId = siteReports?.filter(
                                    report => report?.lotNumber?.toString() === e.target.value
                                  )?.[0]?.jobId;
                                  setFieldValue('lot', e.target.value);
                                  setFieldValue('jobId', jobId);
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    sx: {
                                      maxHeight: '30rem'
                                    }
                                  }
                                }}
                              >
                                {(!siteReports || siteReports.length) === 0 && (
                                  <MenuItem disabled>No active lots</MenuItem>
                                )}
                                {siteReports &&
                                  siteReports.map((lot, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={`${lot.lotNumber}`}>
                                        {lot.lotNumber}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesPlansDropdown
                              isRequired={true}
                              isDisabled={isLoading || !values.lot}
                              value={values.plans}
                              apiParams={{
                                neighborhoodId: Number(values.neighborhoods),
                                lotNumber: Number(values.lot),
                                forMarketingMaterials: true
                              }}
                              isEnabled={!!values.neighborhoods && !!values.lot}
                              handleChange={(planId, neighborhoodSeriesId) => {
                                setFieldValue('plans', planId);
                                setFieldValue('elevations', '');
                                if (planId && neighborhoodSeriesId) {
                                  fetchElevationsList(planId, neighborhoodSeriesId);
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <FormControl fullWidth variant="outlined" size="medium" required>
                              <SMBSelect
                                disabled={!values.plans || isLoading}
                                label={'Elevation'}
                                name="elevations"
                                value={values.elevations}
                                variant="outlined"
                                required
                                onChange={e => {
                                  setFieldValue('elevations', e.target.value);
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    sx: {
                                      maxHeight: '30rem'
                                    }
                                  }
                                }}
                              >
                                {(!elevationList || elevationList.length) === 0 && (
                                  <MenuItem disabled>No Elevations to list</MenuItem>
                                )}
                                {elevationList?.map((elevation, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={elevation.value}>
                                      {elevation.description}
                                    </MenuItem>
                                  );
                                })}
                              </SMBSelect>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={'auto'}>
                            <LoadingButton
                              id="generate-contract-documents"
                              type="submit"
                              color="primary"
                              variant="contained"
                              disabled={isLoading || !values.elevations}
                              loading={isLoading}
                              startIcon={<Description />}
                              className={classes.generateBtn}
                            >
                              Generate
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </Form>
                      <Box mt={1}>
                        {documentTypes && documentTypes.length !== 0 && (
                          <BulkDownloadTable
                            data={documentTypes}
                            downloadApiParams={{
                              jobId: Number(values.jobId),
                              planId: Number(values.plans),
                              planElevationId: Number(values.elevations)
                            }}
                            downloadApiRequest={(docs, filters) =>
                              postSampleContractDocuments(docs, filters)
                            }
                            bundleLabel="sample-contract-documents.zip"
                            isLoading={isGeneratingDocs}
                            selectedDocs={selectedDocs}
                            setSelectedDocs={setSelectedDocs}
                          />
                        )}
                      </Box>
                    </>
                  );
                }}
              </Formik>
            </Box>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  generateBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));
